import React from "react";
import { ShieldCheck, FileText, AlertTriangle, IndianRupee, Database, Cookie, UserCheck, Mail } from "lucide-react";

const SECTIONS = [
  {
    icon: Database,
    title: "1. Information We Collect",
    points: [
      "Identity details: full name, email address, and 10-digit Indian mobile number provided at signup.",
      "KYC documents: PAN, Aadhaar / address proof, bank account or UPI details submitted for verification.",
      "Usage data: campaigns browsed, referral links created, applications submitted, and wallet transactions.",
      "Technical data: device, browser, and IP logs used for security and fraud prevention.",
    ],
  },
  {
    icon: UserCheck,
    title: "2. How We Use Your Information",
    points: [
      "To create and manage your account, permanent User ID, and referral network.",
      "To verify KYC before enabling withdrawals, as required for financial platforms in India.",
      "To credit referral rewards, signup bonus points, and process withdrawal requests.",
      "To share your application details with the respective bank / NBFC / partner institution whose product you apply for.",
      "To send important service notifications (reward status, withdrawal updates, KYC decisions).",
    ],
  },
  {
    icon: IndianRupee,
    title: "3. Campaign Payments & Client Reports (Important)",
    highlight: true,
    points: [
      "Any work done by a user on a campaign (lead, application, signup, or conversion) is eligible for payment ONLY when it is recorded and confirmed in the respective client's / partner's official report.",
      "If a conversion does not appear in the client report, or is marked rejected, invalid, duplicate, or fraudulent by the client, no payout will be due for that entry — even if it was tracked on our platform.",
      "Rewards shown as 'pending' in your wallet are provisional and become payable only after client report confirmation and internal approval.",
      "Credit Kendra's decision based on the client report will be final for reward eligibility. Chargebacks or reversals by the client may lead to reversal of already credited rewards.",
      "Signup bonus points are promotional. Only the percentage configured by the admin (currently shown on the withdrawal page) is added to each withdrawal; the full bonus is not directly withdrawable.",
    ],
  },
  {
    icon: ShieldCheck,
    title: "4. Data Sharing & Security",
    points: [
      "We share application data only with the partner institution whose product you choose to apply for.",
      "We do not sell your personal data to third parties for marketing.",
      "Data is stored on secure servers with encrypted transport (HTTPS) and hashed passwords.",
      "Documents are accessed only by authorised verification staff for KYC review.",
    ],
  },
  {
    icon: Cookie,
    title: "5. Cookies",
    points: [
      "We use secure, httpOnly cookies to keep you signed in and protect your session.",
      "Referral attribution cookies/parameters help us credit the correct referrer.",
    ],
  },
  {
    icon: FileText,
    title: "6. Your Rights",
    points: [
      "You may request correction of your profile details from the dashboard.",
      "You may request account closure by contacting support; wallet balances will be settled as per withdrawal rules after KYC.",
      "KYC edits after verification require re-verification for your security.",
    ],
  },
  {
    icon: AlertTriangle,
    title: "7. Policy Updates",
    points: [
      "We may update this policy from time to time. Continued use of the platform after changes means you accept the updated policy.",
      "Significant changes will be communicated via in-app notification or email.",
    ],
  },
];

export default function PrivacyPolicy() {
  return (
    <div className="bg-[#F8FAFC]" data-testid="privacy-policy-page">
      <div className="bg-[#0B223D] text-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <p className="text-teal-400 font-semibold text-sm tracking-widest uppercase">Legal</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold mt-2">Privacy Policy</h1>
          <p className="text-white/70 mt-4 max-w-2xl text-base">
            Credit Kendra ("Credit Kendra", "we", "us") respects your privacy. This policy explains what data we collect,
            how we use it, and the rules that govern campaign payouts. Last updated: June 2026.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-6">
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6" data-testid="privacy-payment-clause">
          <div className="flex items-start gap-3">
            <IndianRupee className="w-6 h-6 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <h2 className="font-display font-bold text-lg text-amber-900">Payment Rule — Please Read Carefully</h2>
              <p className="text-amber-800 text-sm mt-1.5 leading-relaxed">
                Jo bhi user kisi campaign par kaam karta hai, uska payment <strong>tabhi hoga jab wo kaam client ki official report me aayega</strong>.
                Client report me confirm na hone par us entry ka payment nahi banega. Pending rewards provisional hote hain.
              </p>
            </div>
          </div>
        </div>

        {SECTIONS.map((s) => (
          <div key={s.title} className={`rounded-2xl border p-7 ${s.highlight ? "bg-amber-50/60 border-amber-200" : "bg-white border-slate-200"}`} data-testid={`privacy-section-${s.title.split(".")[0]}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${s.highlight ? "bg-amber-100 text-amber-700" : "bg-teal-50 text-teal-600"}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <h2 className="font-display font-bold text-lg text-[#0B223D]">{s.title}</h2>
            </div>
            <ul className="space-y-2.5">
              {s.points.map((p, i) => (
                <li key={i} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <span className={`w-1.5 h-1.5 rounded-full mt-2 shrink-0 ${s.highlight ? "bg-amber-500" : "bg-teal-500"}`} />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="bg-white border border-slate-200 rounded-2xl p-7" data-testid="privacy-contact">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-[#0B223D]"><Mail className="w-5 h-5" /></div>
            <h2 className="font-display font-bold text-lg text-[#0B223D]">8. Contact Us</h2>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed">
            Privacy-related questions ke liye likhein: <a href="mailto:support@creditkendra.com" className="font-semibold text-teal-700">support@creditkendra.com</a> ya
            call/WhatsApp <a href="tel:+91727662216" className="font-semibold text-teal-700">+91 98235 67811</a>.
          </p>
          <p className="text-xs text-slate-400 mt-3">
            Credit Kendra, 302, Gokul Plaza, Veer Savarkar Marg, above Central Bank of India, Gawad Wadi, Virar East, Virar, Maharashtra 401305
          </p>
        </div>
      </div>
    </div>
  );
}
